import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Mail, Linkedin, Phone, Send } from "lucide-react";
import { SectionHeader, Reveal, Pill } from "@/components/Bits";
import { PROFILE } from "@/data/portfolio";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error("Please fill in all fields.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      if (res.data?.email_sent) {
        toast.success("Message sent! Nikita will get back to you soon.");
      } else {
        toast.success("Message received! It's saved and Nikita will be notified shortly.");
      }
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="max-w-6xl mx-auto px-6 md:px-10 pt-16 md:pt-24">
      <SectionHeader
        eyebrow="Let's talk"
        title="Got a product idea?"
        emphasis="Let's ship it."
        sub="Whether you want to collaborate, hire, or just chat about gamification and growth loops — drop me a note."
      />

      <div className="grid lg:grid-cols-5 gap-7">
        <div className="lg:col-span-2 flex flex-col gap-5">
          <Reveal>
            <div className="soft-card p-7" data-testid="contact-info-card">
              <Pill variant="peach">✨ Reach out</Pill>
              <p className="mt-5 text-[11px] uppercase tracking-[0.18em] font-semibold text-[var(--text-muted)]">Direct lines</p>
              <div className="mt-4 flex flex-col gap-3">
                <a data-testid="contact-email" href={`mailto:${PROFILE.email}`} className="flex items-center gap-3 font-medium hover:text-[var(--peach)] transition-colors">
                  <span className="w-10 h-10 rounded-full bg-[var(--peach-soft)] flex items-center justify-center"><Mail className="w-4 h-4" /></span>
                  {PROFILE.email}
                </a>
                <a data-testid="contact-linkedin" href={PROFILE.linkedin} target="_blank" rel="noreferrer" className="flex items-center gap-3 font-medium hover:text-[var(--peach)] transition-colors">
                  <span className="w-10 h-10 rounded-full bg-[var(--lavender-soft)] flex items-center justify-center"><Linkedin className="w-4 h-4" /></span>
                  LinkedIn
                </a>
                <a data-testid="contact-phone" href="tel:+917024878835" className="flex items-center gap-3 font-medium hover:text-[var(--peach)] transition-colors">
                  <span className="w-10 h-10 rounded-full bg-[var(--sage-soft)] flex items-center justify-center"><Phone className="w-4 h-4" /></span>
                  {PROFILE.phone}
                </a>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.08}>
            <div className="soft-card p-7 bg-[var(--peach-soft)]">
              <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[var(--text-muted)]">Response time</p>
              <p className="mt-3 font-display text-2xl font-medium">Usually within 24 hours.</p>
              <p className="mt-2 text-sm text-[var(--text-muted)]">Faster on weekdays, with extra coffee.</p>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-3">
          <Reveal delay={0.05}>
            <form onSubmit={onSubmit} data-testid="contact-form" className="soft-card p-7 md:p-8 flex flex-col gap-5">
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[var(--text-muted)]">Your name</label>
                  <input data-testid="contact-input-name" name="name" value={form.name} onChange={onChange} placeholder="Jane Doe" className="mt-2 soft-input" />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[var(--text-muted)]">Email</label>
                  <input data-testid="contact-input-email" type="email" name="email" value={form.email} onChange={onChange} placeholder="jane@company.com" className="mt-2 soft-input" />
                </div>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[var(--text-muted)]">Subject</label>
                <input data-testid="contact-input-subject" name="subject" value={form.subject} onChange={onChange} placeholder="Let's build something delightful" className="mt-2 soft-input" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[var(--text-muted)]">Message</label>
                <textarea data-testid="contact-input-message" name="message" rows={6} value={form.message} onChange={onChange} placeholder="Tell me about your product or idea…" className="mt-2 soft-input resize-y" />
              </div>

              <button type="submit" disabled={loading} data-testid="contact-submit-btn" className="btn-accent disabled:opacity-70 disabled:cursor-not-allowed mt-2 justify-center">
                {loading ? (<><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>) : (<>Send message <Send className="w-4 h-4" /></>)}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </div>
  );
}
